import { router } from '@inertiajs/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'

interface Props {
  plan_tiers: string[]
  auth: { user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean } }
}

function New({ plan_tiers }: Props) {
  const { t } = useTranslation('admin')
  const [submitting, setSubmitting] = useState(false)

  const [form, setForm] = useState({
    name: '',
    code: '',
    plan_tier: 'pro',
    duration_value: '3',
    duration_unit: 'hours',
    max_redemptions: '10',
    bonus_credits: '0',
    expires_at: '',
  })

  const computeDurationHours = (): number => {
    const value = parseInt(form.duration_value) || 0
    switch (form.duration_unit) {
      case 'hours': return value
      case 'days': return value * 24
      case 'weeks': return value * 168
      default: return value
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    router.post('/admin/access_codes', {
      name: form.name,
      code: form.code || undefined,
      plan_tier: form.plan_tier,
      duration_hours: computeDurationHours(),
      max_redemptions: parseInt(form.max_redemptions),
      bonus_credits: parseInt(form.bonus_credits) || 0,
      expires_at: form.expires_at || undefined,
    }, {
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              <div className="mx-auto max-w-2xl">
                <h1 className="mb-6 text-3xl font-bold text-foreground">{t('accessCodes.new.title')}</h1>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('accessCodes.new.title')}</CardTitle>
                    <CardDescription>{t('accessCodes.new.description')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('accessCodes.new.form.name')}</Label>
                        <Input
                          id="name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder={t('accessCodes.new.form.namePlaceholder')}
                          required
                        />
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.nameDescription')}</p>
                      </div>

                      {/* Custom Code */}
                      <div className="space-y-2">
                        <Label htmlFor="code">{t('accessCodes.new.form.code')}</Label>
                        <Input
                          id="code"
                          value={form.code}
                          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                          placeholder={t('accessCodes.new.form.codePlaceholder')}
                          className="font-mono"
                        />
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.codeDescription')}</p>
                      </div>

                      {/* Plan Tier */}
                      <div className="space-y-2">
                        <Label>{t('accessCodes.new.form.planTier')}</Label>
                        <Select value={form.plan_tier} onValueChange={(v) => setForm({ ...form, plan_tier: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {plan_tiers.map((tier) => (
                              <SelectItem key={tier} value={tier} className="capitalize">
                                {tier.charAt(0).toUpperCase() + tier.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.planTierDescription')}</p>
                      </div>

                      {/* Duration */}
                      <div className="space-y-2">
                        <Label>{t('accessCodes.new.form.durationValue')}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={form.duration_value}
                            onChange={(e) => setForm({ ...form, duration_value: e.target.value })}
                            className="w-32"
                            required
                          />
                          <Select value={form.duration_unit} onValueChange={(v) => setForm({ ...form, duration_unit: v })}>
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hours">{t('accessCodes.new.form.hours')}</SelectItem>
                              <SelectItem value="days">{t('accessCodes.new.form.days')}</SelectItem>
                              <SelectItem value="weeks">{t('accessCodes.new.form.weeks')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.durationDescription')}</p>
                      </div>

                      {/* Max Redemptions */}
                      <div className="space-y-2">
                        <Label htmlFor="max_redemptions">{t('accessCodes.new.form.maxRedemptions')}</Label>
                        <Input
                          id="max_redemptions"
                          type="number"
                          min="1"
                          value={form.max_redemptions}
                          onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                          className="w-32"
                          required
                        />
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.maxRedemptionsDescription')}</p>
                      </div>

                      {/* Bonus Credits */}
                      <div className="space-y-2">
                        <Label htmlFor="bonus_credits">{t('accessCodes.new.form.bonusCredits')}</Label>
                        <Input
                          id="bonus_credits"
                          type="number"
                          min="0"
                          value={form.bonus_credits}
                          onChange={(e) => setForm({ ...form, bonus_credits: e.target.value })}
                          className="w-32"
                        />
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.bonusCreditsDescription')}</p>
                      </div>

                      {/* Expires At */}
                      <div className="space-y-2">
                        <Label htmlFor="expires_at">{t('accessCodes.new.form.expiresAt')}</Label>
                        <Input
                          id="expires_at"
                          type="datetime-local"
                          value={form.expires_at}
                          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                        />
                        <p className="text-xs text-muted-foreground">{t('accessCodes.new.form.expiresAtDescription')}</p>
                      </div>

                      {/* Submit */}
                      <div className="flex justify-end">
                        <Button type="submit" disabled={submitting || !form.name}>
                          {submitting ? t('accessCodes.new.creating') : t('accessCodes.new.submit')}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
